import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      setOrganization(res.data.organization);
      setProjects(res.data.projects || []);
      const saved = localStorage.getItem('hai_current_project');
      if (saved && res.data.projects?.find(p => p.id === saved)) {
        setCurrentProject(res.data.projects.find(p => p.id === saved));
      } else if (res.data.projects?.length > 0) {
        setCurrentProject(res.data.projects[0]);
        localStorage.setItem('hai_current_project', res.data.projects[0].id);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('hai_token');
    if (token) {
      fetchMe();
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('hai_token', res.data.token);
    localStorage.setItem('hai_refresh_token', res.data.refresh_token);
    setUser(res.data.user);
    await fetchMe();
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    localStorage.setItem('hai_token', res.data.token);
    localStorage.setItem('hai_refresh_token', res.data.refresh_token);
    setUser(res.data.user);
    await fetchMe();
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('hai_token');
    localStorage.removeItem('hai_refresh_token');
    localStorage.removeItem('hai_current_project');
    setUser(null);
    setOrganization(null);
    setProjects([]);
    setCurrentProject(null);
  };

  const switchProject = (project) => {
    setCurrentProject(project);
    localStorage.setItem('hai_current_project', project.id);
  };

  return (
    <AuthContext.Provider value={{ user, organization, projects, currentProject, loading, login, register, logout, switchProject, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return ctx;
};
